import React, { useRef, useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';

import {
  TextField,
  MenuItem,
  FormHelperText,
  IconButton,
} from '@material-ui/core';
import { Edit } from '@material-ui/icons';
import { useField } from '@unform/core';
import { isArray } from 'lodash';
import PropTypes from 'prop-types';
import qs from 'querystring';

import { Modal } from '~/components';
import { t } from '~/helpers';
import api from '~/services/api';

import { Profiles } from './components';
import * as S from './styles';

export default function CRUDProfile({
  name,
  report,
  nameReport,
  onChange,
  ...rest
}) {
  const {
    fieldName,
    defaultValue,
    registerField,
    error,
    clearError,
  } = useField(name);

  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [preview, setPreview] = useState(defaultValue || 0);

  const inputRef = useRef(null);
  const modalRef = useRef(null);

  const handleGetAPI = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await api.get('/perfisrelatorios', {
        params: {
          wherefield: ['idgrupocamposistema'],
          valuewherefield: [report],
          listatodos: 1,
          pesquisalista: true,
        },
        paramsSerializer: (param) => qs.stringify(param),
      });

      setOptions(
        data.items.map((item) => ({
          label: item.nomeperfilrelatorio,
          value: item.idperfilrelatorio,
        }))
      );
    } catch (err) {
      if (isArray(err)) err.map((e) => toast.error(t(e)));
      else toast.error(t(err));
    } finally {
      setLoading(false);
    }
  }, [report]);

  const handleChangeInput = useCallback((value) => {
    inputRef.current.value = value;
    setPreview(value);
    if (onChange) {
      onChange(value);
    }
  }, [onChange]);

  const ProfilesModalComponent = useCallback(
    (props) => <Profiles {...props} report={report} nameReport={nameReport} />,
    [report, nameReport]
  );

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'value',
      getValue: () => preview,
      clearValue: (ref) => {
        ref.value = '';
        setPreview('');
      },
      setValue: (ref, value) => {
        ref.value = value;
        setPreview(value);
      },
    });
  }, [registerField, fieldName, preview]);

  useEffect(() => {
    handleGetAPI();
  }, [handleGetAPI]);

  return (
    <S.Wrapper error={!!error}>
      <div className="__group">
        <TextField
          fullWidth
          select
          defaultValue={defaultValue || 0}
          value={preview}
          disabled={loading}
          onFocus={clearError}
          onChange={({ target }) => handleChangeInput(target.value)}
          error={!!error}
          variant="outlined"
          size="small"
          InputLabelProps={{ shrink: true }}
          {...rest}
        >
          {options.map((option, index) => (
            <MenuItem key={index} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <IconButton
          size="small"
          disabled={loading}
          onClick={() => modalRef.current.handleOpenModal()}
        >
          <Edit size={20} />
        </IconButton>
      </div>

      <FormHelperText>{error}</FormHelperText>

      <input type="hidden" ref={inputRef} defaultValue={defaultValue} />

      <Modal
        ref={modalRef}
        disableBackdropClick
        disableEscapeKeyDown
        onClose={handleGetAPI}
        loading={loading}
        fullScreen={window.innerWidth <= 425}
        title={t('s.menu.cadastro.usuario.perfil')}
        maxWidth="md"
        component={ProfilesModalComponent}
        actions={[
          {
            label: 'Fechar',
            color: 'secondary',
            variant: 'text',
            autoFocus: true,
            callback: () => modalRef.current.handleCloseModal(),
          },
        ]}
      />
    </S.Wrapper>
  );
}

CRUDProfile.propTypes = {
  name: PropTypes.string.isRequired,
  report: PropTypes.number.isRequired,
  nameReport: PropTypes.string.isRequired,
};
